import * as React from 'react';
import { type AvailabilityProfile, type AvailabilityTimeEncoding } from '@ooh/contracts';
import type { PeriodLabelMapping } from '../components/period-label-mapping-table';
import { getDistinctColumnValues } from '../lib/period-label-values';
import { isManuallySelectableEncoding } from '../lib/manual-encodings';
import { deriveUnmappedStatusTokens } from '../lib/derive-status-tokens';
import type { SetProfile } from './profile-editor-types';
import { useDateRangeEditor } from './use-date-range-editor';
import { useMonthPeriodEditor } from './use-month-period-editor';

/**
 * statusDictionary: the operator-facing editor for
 * `StatusTokenMappingTable` (CU-86cb1npz2, gap #3) — maps a raw, currently
 * unmapped status token to FREE/UNAVAILABLE. `unmappedStatusTokens` is
 * derived fresh every render from the CURRENT profile (see
 * `deriveUnmappedStatusTokens`'s doc comment): once a token is mapped, it
 * resolves via `profile.statusDictionary` on the next render and naturally
 * drops out of the list.
 */
function useStatusDictionaryEditor(
  profile: AvailabilityProfile,
  setProfile: SetProfile,
  sampleRows: Record<string, string>[],
) {
  const unmappedStatusTokens = deriveUnmappedStatusTokens(profile, sampleRows);

  function updateStatusDictionaryEntry(rawToken: string, next: 'FREE' | 'UNAVAILABLE' | '') {
    setProfile((p) => {
      if (next === '') {
        const rest = { ...p.statusDictionary };
        delete rest[rawToken];
        return { ...p, statusDictionary: rest };
      }
      return { ...p, statusDictionary: { ...p.statusDictionary, [rawToken]: next } };
    });
  }

  return { unmappedStatusTokens, updateStatusDictionaryEntry };
}

/** global: the required whole-file date-range fields when globalPeriod is still unresolved. */
function useGlobalRangeEditor(
  setProfile: SetProfile,
  initialGlobalPeriod: { start: string; end: string } | null,
) {
  const [globalDateFrom, setGlobalDateFrom] = React.useState(initialGlobalPeriod?.start ?? '');
  const [globalDateTo, setGlobalDateTo] = React.useState(initialGlobalPeriod?.end ?? '');

  function handleGlobalRangeChange(next: { from: string; to: string }) {
    setGlobalDateFrom(next.from);
    setGlobalDateTo(next.to);
    setProfile((p) => ({
      ...p,
      globalPeriod: next.from && next.to ? { start: next.from, end: next.to } : null,
    }));
  }

  return { globalDateFrom, globalDateTo, handleGlobalRangeChange };
}

/**
 * The "Dostawca" (supplier) selector: writes to the profile's
 * `defaultSupplierName` — the whole-file supplier fallback used when
 * `supplierColumn` is null (see availability-profile-resolution /
 * AvailabilityProfile shared descriptor). NOT local-only UI state. When
 * `supplierColumn` IS set (a multi-supplier file with a per-row Dostawca
 * column), the selector instead shows the distinct per-row values found in
 * the sample so far, purely informational.
 */
function useSupplierEditor(
  profile: AvailabilityProfile,
  setProfile: SetProfile,
  sampleRows: Record<string, string>[],
  realSupplierOptions: { value: string; label: string }[],
) {
  function handleSupplierNumberColumnChange(next: string) {
    setProfile((p) => ({ ...p, idColumns: { ...p.idColumns, supplierNumberColumn: next } }));
  }

  function handleSupplierNameChange(next: string) {
    setProfile((p) => ({ ...p, defaultSupplierName: next || null }));
  }

  const supplierOptions = profile.supplierColumn
    ? getDistinctColumnValues(sampleRows, profile.supplierColumn).map((v) => ({
        value: v,
        label: v,
      }))
    : realSupplierOptions;

  const supplierIncomplete =
    profile.supplierColumn === null && !profile.defaultSupplierName?.trim();

  return {
    supplierName: profile.defaultSupplierName ?? '',
    setSupplierName: handleSupplierNameChange,
    supplierOptions,
    supplierIncomplete,
    handleSupplierNumberColumnChange,
  };
}

/**
 * period_label_map: one editable date-range row per distinct raw phrase.
 * Sourced from `columnDistinctValues` (the upload response's full-file
 * bounded distinct values, see `getDistinctColumnValues`) when it covers
 * `periodLabelColumn`, else falls back to sampleRows.
 */
function usePeriodLabelEditor(
  profile: AvailabilityProfile,
  setProfile: SetProfile,
  sampleRows: Record<string, string>[],
  columnDistinctValues: Record<string, string[]>,
) {
  const distinctPeriodLabels = getDistinctColumnValues(
    sampleRows,
    profile.periodLabelColumn,
    columnDistinctValues,
  );
  const periodLabelMappings: PeriodLabelMapping[] = distinctPeriodLabels.map((label) => {
    const existing = profile.periodLabelMap?.[label];
    return { label, from: existing?.start ?? '', to: existing?.end ?? '' };
  });
  // An empty distinct-labels list (e.g. lost sampleRows after a page reload,
  // see availability-import-wizard.tsx) must never read as "complete" —
  // Array.prototype.every is vacuously true on [] and would otherwise
  // misleadingly enable Confirm with nothing actually mapped.
  const allPeriodLabelsMapped =
    distinctPeriodLabels.length > 0 &&
    distinctPeriodLabels.every((label) => {
      const entry = profile.periodLabelMap?.[label];
      return entry !== undefined && entry.start !== '' && entry.end !== '';
    });

  function updatePeriodLabelMapping(label: string, field: 'from' | 'to', value: string) {
    setProfile((p) => {
      const current = p.periodLabelMap ?? {};
      const entry = current[label] ?? { start: '', end: '' };
      const nextEntry = field === 'from' ? { ...entry, start: value } : { ...entry, end: value };
      return { ...p, periodLabelMap: { ...current, [label]: nextEntry } };
    });
  }

  /** Changing the source column invalidates every prior mapping entry — the
   * distinct raw values come from a different column now, so a stale map
   * keyed by the old column's phrases could otherwise linger unused. */
  function handlePeriodLabelColumnChange(next: string) {
    setProfile((p) => ({ ...p, periodLabelColumn: next || null, periodLabelMap: {} }));
  }

  return {
    periodLabelMappings,
    allPeriodLabelsMapped,
    updatePeriodLabelMapping,
    handlePeriodLabelColumnChange,
  };
}

/** addressColumns: the (Adres, Miasto) column pickers now rendered as rows
 * in `ColumnMappingTable` (see preview-phase.tsx's `buildColumnMappingRows`).
 * `addressColumns` is a required, non-nullable object on `AvailabilityProfile`
 * — only its two leaf fields are nullable strings. */
function useAddressEditor(setProfile: SetProfile) {
  function handleAddressColumnChange(next: string) {
    setProfile((p) => ({ ...p, addressColumns: { ...p.addressColumns, addressColumn: next } }));
  }

  function handleCityColumnChange(next: string) {
    setProfile((p) => ({ ...p, addressColumns: { ...p.addressColumns, cityColumn: next } }));
  }

  return { handleAddressColumnChange, handleCityColumnChange };
}

/**
 * Lets the operator manually override a low-confidence resolver guess by
 * picking a different time encoding, resetting the fields exclusive to
 * whichever encoding is being left. `half_month` is never offered as a
 * target — see `MANUALLY_SELECTABLE_ENCODINGS` (`lib/manual-encodings.ts`).
 */
function useTimeEncodingEditor(
  profile: AvailabilityProfile,
  setProfile: SetProfile,
  resetMonthLabels: () => void,
) {
  const canOverrideTimeEncoding = isManuallySelectableEncoding(profile.timeEncoding);

  function setTimeEncoding(next: AvailabilityTimeEncoding) {
    if (profile.timeEncoding === next) return;
    // `periodColumns` gets reset to [] below whenever the encoding actually
    // changes — `monthLabels` (a separate hook's state, see
    // `useMonthPeriodEditor`) must reset alongside it or a manually-added
    // row after a month round-trip inherits a stale label (review round 1).
    resetMonthLabels();
    setProfile((p) => {
      if (p.timeEncoding === next) return p;
      const base: AvailabilityProfile = {
        ...p,
        timeEncoding: next,
        globalPeriod: null,
        dateRangeColumns: null,
        periodLabelColumn: null,
        periodLabelMap: null,
        // Exclusive to month/half_month — a manually-built row list from a
        // previous 'month' round-trip must not resurface once the operator
        // switches away and back (CU-86cb1npz2). `next === 'month'` starts
        // from this same empty array, ready for `addPeriodRow`.
        periodColumns: [],
      };
      if (next === 'date_range') {
        return { ...base, dateRangeColumns: { startColumn: '', endColumn: '' } };
      }
      if (next === 'period_label_map') {
        return { ...base, periodLabelMap: {} };
      }
      return base;
    });
  }

  return { canOverrideTimeEncoding, setTimeEncoding };
}

/** Wraps the raw encoding switch to re-apply the operator's manual
 * whole-file range when returning to `global`. `setTimeEncoding` clears
 * `globalPeriod` on every switch (exclusive-field reset), but the date
 * fields keep their local values (`useGlobalRangeEditor`) — without this
 * re-seed the fields displayed a picked range while the profile said
 * "no range": OKRES rendered "—" and Confirm stayed blocked on a form
 * that looked complete (CU-86canaqmw). Extracted to its own hook to keep
 * `usePreviewProfileEditor` under the repo's per-function line-count budget. */
function useSyncedTimeEncoding(
  setProfile: SetProfile,
  timeEncodingEditor: ReturnType<typeof useTimeEncodingEditor>,
  globalRange: ReturnType<typeof useGlobalRangeEditor>,
) {
  function setTimeEncodingSynced(next: AvailabilityTimeEncoding) {
    timeEncodingEditor.setTimeEncoding(next);
    if (next === 'global' && globalRange.globalDateFrom && globalRange.globalDateTo) {
      setProfile((p) =>
        p.timeEncoding === 'global'
          ? {
              ...p,
              globalPeriod: {
                start: globalRange.globalDateFrom,
                end: globalRange.globalDateTo,
              },
            }
          : p,
      );
    }
  }

  return { setTimeEncodingSynced };
}

interface ConfirmGatingBooleans {
  globalRangeIncomplete: boolean;
  periodLabelIncomplete: boolean;
  monthPeriodIncomplete: boolean;
  dateRangeIncomplete: boolean;
  showMarkRestUnavailable: boolean;
}

/** Derives the per-encoding confirm-gating booleans from the current
 * profile — extracted from `usePreviewProfileEditor` to keep that function
 * under the repo's per-function complexity budget. */
function deriveConfirmGating(
  profile: AvailabilityProfile,
  allPeriodLabelsMapped: boolean,
): ConfirmGatingBooleans {
  const globalRangeIncomplete = profile.timeEncoding === 'global' && profile.globalPeriod === null;
  const periodLabelIncomplete =
    profile.timeEncoding === 'period_label_map' && !allPeriodLabelsMapped;
  // An operator can clear a month/half_month period row's from/to date to an
  // empty string in the editable period-columns table (updatePeriodRow) —
  // that row must block Confirm the same way an incomplete global range or
  // period-label mapping does.
  const monthPeriodIncomplete =
    (profile.timeEncoding === 'month' || profile.timeEncoding === 'half_month') &&
    profile.periodColumns.some((pc) => pc.periodStart === '' || pc.periodEnd === '');
  // The manual encoding-override selector can leave `date_range` with an
  // empty startColumn/endColumn scaffold (see `useTimeEncodingEditor`) — a
  // state the server-side resolver alone could never produce (it always
  // assigns real column names), so this path was previously unreachable and
  // ungated. Blocks Confirm until both columns are picked.
  const dateRangeIncomplete =
    profile.timeEncoding === 'date_range' &&
    (!profile.dateRangeColumns?.startColumn || !profile.dateRangeColumns?.endColumn);
  const showMarkRestUnavailable =
    profile.timeEncoding === 'global' && profile.polarity === 'list_of_free';

  return {
    globalRangeIncomplete,
    periodLabelIncomplete,
    monthPeriodIncomplete,
    dateRangeIncomplete,
    showMarkRestUnavailable,
  };
}

/**
 * Owns every editable field of the availability-import preview's resolved
 * profile, plus the derived confirm-gating booleans. Composed from smaller
 * per-encoding hooks (above) to keep each within the repo's per-function
 * complexity/line-count lint budget.
 */
export function usePreviewProfileEditor(
  initialProfile: AvailabilityProfile,
  sampleRows: Record<string, string>[],
  realSupplierOptions: { value: string; label: string }[] = [],
  columnDistinctValues: Record<string, string[]> = {},
) {
  const [profile, setProfile] = React.useState<AvailabilityProfile>(initialProfile);
  const [markRestUnavailable, setMarkRestUnavailable] = React.useState(false);
  const [initialMonthColumns] = React.useState(() =>
    initialProfile.periodColumns.map((pc) => pc.column),
  );

  const dateRange = useDateRangeEditor(setProfile);
  const monthPeriod = useMonthPeriodEditor(profile, setProfile, initialMonthColumns);
  const globalRange = useGlobalRangeEditor(setProfile, initialProfile.globalPeriod);
  const periodLabel = usePeriodLabelEditor(profile, setProfile, sampleRows, columnDistinctValues);
  const supplier = useSupplierEditor(profile, setProfile, sampleRows, realSupplierOptions);
  const address = useAddressEditor(setProfile);
  const statusDictionary = useStatusDictionaryEditor(profile, setProfile, sampleRows);
  const timeEncodingEditor = useTimeEncodingEditor(
    profile,
    setProfile,
    monthPeriod.resetMonthLabels,
  );
  const { setTimeEncodingSynced } = useSyncedTimeEncoding(
    setProfile,
    timeEncodingEditor,
    globalRange,
  );

  const confirmGating = deriveConfirmGating(profile, periodLabel.allPeriodLabelsMapped);

  return {
    profile,
    markRestUnavailable,
    setMarkRestUnavailable,
    ...confirmGating,
    ...supplier,
    ...address,
    ...dateRange,
    ...monthPeriod,
    ...globalRange,
    ...statusDictionary,
    ...timeEncodingEditor,
    setTimeEncoding: setTimeEncodingSynced,
    periodLabelMappings: periodLabel.periodLabelMappings,
    updatePeriodLabelMapping: periodLabel.updatePeriodLabelMapping,
    handlePeriodLabelColumnChange: periodLabel.handlePeriodLabelColumnChange,
  };
}
